/*
 * -----------------------------------------------------------------------
 *  <copyright file="study-indications-grid.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { orderBy } from '@progress/kendo-data-query';

import { useBoolean, useGridCells, useTranslation } from 'hooks';
import { StudyFields } from 'queries/generated/fragments.generated';
import { GET_ORGANISATION_PROGRAM_PRODUCT } from 'queries/uam';

import DeleteIndicationDialog from 'views/components/admin/studies/indications-dialogs/delete-indication-dialog';
import ExpandableGrid from 'views/components/core/expandable/grid';
import { GridColumnMenuActiveFilter } from 'views/components/core/grid-filters';

interface StudyIndicationsGridInterface {
    study: StudyFields;
    canEdit: boolean;
}

const StudyIndicationsGrid = ({ study, canEdit }: StudyIndicationsGridInterface) => {
    const { t } = useTranslation();
    const [currentIndication, setCurrentIndication] = useState(null);
    const showDeleteProductDialog = useBoolean(false);
    const [loadData, { data }] = useLazyQuery(GET_ORGANISATION_PROGRAM_PRODUCT);
    const { MaxLengthCell, YesNoCell } = useGridCells();

    useEffect(() => {
        if (study) loadData();
    }, [study]);

    const actions = (item: any) => {
        if (!item._isProgram)
            return [
                {
                    label: 'admin:unassignIndication',
                    icon: ['fal', 'link-slash'],
                    onClick: () => {
                        setCurrentIndication(item);
                        showDeleteProductDialog.setTrue();
                    },
                    enabled: canEdit,
                },
            ];
        return null;
    };

    const getAvailableIndications = useMemo((): any[] => {
        // Items can be duplicated
        let availableStudyIndications = data?.organisation?.indications
            .filter((product: any) => study.indicationsIds.includes(product.id))
            .map((product: any) => ({ ...product, _isProgram: false }));

        const availablePrograms = data?.organisation?.programs.filter((program: any) =>
            study.programsIds.includes(program.id),
        );
        const availableProgramIndicationsIds =
            availablePrograms?.reduce((result: any, program: any) => result.concat(program.indicationIds), []) || [];
        const availableProgramIndications = data?.organisation?.indications
            .filter((ind: any) => availableProgramIndicationsIds.includes(ind.id))
            .map((ind: any) => ({ ...ind, _isProgram: true }));
        availableStudyIndications = availableStudyIndications?.concat(availableProgramIndications) || [];
        return orderBy(availableStudyIndications, [{ field: 'name', dir: 'asc' }]);
    }, [data]);

    return (
        <>
            <DeleteIndicationDialog
                study={study}
                currentIndication={currentIndication}
                open={showDeleteProductDialog.value}
                onClose={() => {
                    setCurrentIndication(null);
                    showDeleteProductDialog.setFalse();
                }}
            />
            <ExpandableGrid
                customKey="products-grid"
                id="productsGrid"
                key="productsGrid"
                items={getAvailableIndications}
                rowActions={actions}
                groupable={false}
                showPager
                addScroll
                scrollable="scrollable"
                reorderable
                pageable={false}
                resizable
                options={{
                    allowExcelExport: false,
                    allowExpandCollapseAll: false,
                    useFilterChips: false,
                }}
                detail={null}
                columns={[
                    {
                        field: 'name',
                        label: t('admin:indication'),
                        id: 'product-name',
                        minWidth: 140,
                        lockedByDefault: true,
                        cells: { data: MaxLengthCell(70) },
                    },
                    {
                        field: '_isProgram',
                        id: 'product-isProgram',
                        label: t('admin:programIndication'),
                        minWidth: 180,
                        cells: { data: YesNoCell },
                        filterUi: GridColumnMenuActiveFilter,
                    },
                ]}
            />
        </>
    );
};

export default StudyIndicationsGrid;
